/**
 * SubscribeToNotificationsUseCase
 *
 * Live-update use case for the inbox notifications list. Wraps the live
 * collection-group subscription (filtered to the current user's notifications
 * arriving at/after the cursor) and DROPS the same three notification types the
 * page fetch drops (`contentAdded` / `earlyAccessContentAdded` / `liveStarted`),
 * then pipes the surviving `added` / `modified` changes to the caller. A thin
 * type-drop pipe — unlike `SubscribeToChatNodesUseCase` there is NO decryption /
 * peer-resolve step (notifications carry no encrypted body), and no list
 * mutation / sort / badge / Redux here: the `id`-keyed index lookup, the
 * add-vs-update list mutation, the desc-by-timestamp re-sort, and the
 * unread-badge re-check are the consuming hook's job (Task 5).
 *
 * Flutter source: lib/presentation/screens/messages/messages_screen.dart:323-373
 * (`_subscribeToNotificationsUpdates`).
 *   - Query/conditional cursor handled by
 *     `NotificationsService.subscribeToNotifications` (over
 *     `firestoreService.subscribeToNotifications`). `sinceTimestamp` is forwarded
 *     UNCHANGED — it is the most-recent first-page notification's `info.timestamp`
 *     (or `now()` when the first page was empty), resolved by the caller
 *     (messages_screen.dart:324-327).
 *   - messages_screen.dart:345-352 — on EVERY change, if the notification's
 *     `info.notificationType` is `contentAdded` / `earlyAccessContentAdded` /
 *     `liveStarted`, the change is dropped (same set as the page fetch,
 *     messages_screen.dart:177-185; encoded on web as
 *     `DROPPED_NOTIFICATION_TYPES`).
 *   - messages_screen.dart:354-368 — surviving `added` / `modified` changes fan
 *     out to the hook; `removed` is never dispatched by Flutter's notifications
 *     subscription (only `added` / `modified`, lines 358-368), so the hook
 *     ignores any `removed` the service might surface.
 *
 * KNOWN FLUTTER BUG (web ports the corrected behaviour):
 *   Flutter's drop guard runs `return;` (messages_screen.dart:351) from INSIDE
 *   the `for (var change in querySnapshot.docChanges)` loop (opened at line 341).
 *   That `return;` does NOT skip just the one dropped change — it exits the whole
 *   `.listen` callback and terminates the loop, so once a dropped-type change is
 *   hit every LATER change in that same snapshot batch is discarded and
 *   `_checkNotificationsUpdatesBadge()` (line 370) is skipped for that batch. The
 *   adjacent comment at lines 362-363 ("update was made on the notification but
 *   it has 'added' flag because it was not included in the initial query") shows
 *   the loop is meant to keep processing — a `continue` was clearly intended.
 *   Per `.claude/context/conventions.md` → "Handling obvious Flutter bugs", this
 *   one-line predicate fix (`return` → `continue`) is NOT ported as-is: this use
 *   case implements the intended continue-/filter-semantics via `.filter()` —
 *   drop only the matched change, keep processing the rest of the batch.
 *
 * Pure-domain contract:
 *   - The use case owns ONLY the type-drop filter + the pass-through fan-out; the
 *     service stays a thin subscribe + remap.
 *   - The use case does NOT touch `firestoreService` directly and does NOT
 *     dispatch to Redux — both are presentation concerns.
 */

import type { Unsubscribe } from 'firebase/firestore';
import { NotificationsService } from '@/domain/services/notifications/notificationsService';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import type { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';

/**
 * A single live notification change delivered to the caller: the Firestore
 * change type plus the (already remapped) entity.
 */
export interface NotificationChange {
  type: RemoteDocumentChangeType;
  data: AppNotificationEntity;
}

/**
 * Notification types that never enter the rendered list — dropped on every
 * subscription change, identical to the page-fetch drop set
 * `DROPPED_NOTIFICATION_TYPES` in `useInboxNotifications.ts`.
 * Flutter: messages_screen.dart:345-352 (and the same set on the page fetch at
 * messages_screen.dart:177-185).
 */
const DROPPED_NOTIFICATION_TYPES: ReadonlySet<number> = new Set<number>([
  AppNotificationType.contentAdded,
  AppNotificationType.earlyAccessContentAdded,
  AppNotificationType.liveStarted,
]);

export class SubscribeToNotificationsUseCase {
  private notificationsService: NotificationsService;

  constructor(notificationsService: NotificationsService = NotificationsService.getInstance()) {
    this.notificationsService = notificationsService;
  }

  /**
   * Subscribe to live notification updates for `userId`'s inbox. For each
   * delivered batch, changes whose `data.info.notificationType` is in the
   * dropped set are filtered out (continue-semantics — see the file header's
   * "KNOWN FLUTTER BUG" note) and the surviving changes are handed to
   * `onChanges` in arrival order. The list mutation / sort / badge re-check live
   * in the consuming hook (Task 5).
   *
   * @param userId - The current user's Firebase id (the
   *   `info.usersInfo.peerUserId` match on the collection-group query).
   * @param sinceTimestamp - The `>=` cursor on `info.timestamp` (the most-recent
   *   first-page notification's timestamp, or `now()` when the first page was
   *   empty). Forwarded UNCHANGED to the service (messages_screen.dart:324-327).
   * @param onChanges - Called once per snapshot batch with the array of surviving
   *   changes.
   * @returns The Firestore `Unsubscribe` handle from
   *   `NotificationsService.subscribeToNotifications`. The caller (the feature
   *   hook) is responsible for invoking it on cleanup.
   */
  execute(
    userId: string,
    sinceTimestamp: number,
    onChanges: (changes: NotificationChange[]) => void,
  ): Unsubscribe {
    return this.notificationsService.subscribeToNotifications(
      userId,
      sinceTimestamp,
      (rawChanges) => {
        // Drop the three filtered types, KEEP every other change in the batch.
        // `.filter()` is the corrected continue-semantics: Flutter's `return;`
        // (messages_screen.dart:351) would truncate the rest of the batch — see
        // the file-header "KNOWN FLUTTER BUG" note.
        const changes = rawChanges.filter(
          (change) => !DROPPED_NOTIFICATION_TYPES.has(change.data.info.notificationType),
        );
        onChanges(changes);
      },
    );
  }
}
